import React, { useEffect, useState } from 'react';
import { CheckCircle, Copy, KeyRound, ShieldAlert } from 'lucide-react';

/**
 * Die Seite, die ein Kunde aus der Mail heraus öffnet.
 *
 * Sie liegt bewusst **ausserhalb** der Anmeldung: der Empfänger hat noch keinen
 * Zugang, genau darum geht es. Das Token in der Adresse trägt die Berechtigung.
 *
 * Zwei Fassungen, je nachdem, was der Bearbeiter gewählt hat:
 *
 *   anzeigen  Ein Knopf, danach steht das Passwort da — einmal. Wer die Seite
 *             neu lädt, sieht es nicht wieder, und das steht auch dabei.
 *   setzen    Ein Formular. Das gewählte Passwort wird in der laufenden
 *             Instanz gesetzt, nicht erst beim nächsten Ausrollen.
 *
 * Beide zeigen vorher, **um welche Instanz und welche Adresse** es geht. Ohne
 * das setzt jemand mit zwei Instanzen das Passwort der falschen — und merkt es
 * erst, wenn er sich nicht anmelden kann.
 */

const MINDESTLAENGE = 12;

interface Stand {
  modus: 'ANZEIGEN' | 'SETZEN';
  adresse: string | null;
  instanz: string;
}

export default function InstanzZugangPage() {
  const token = new URLSearchParams(window.location.search).get('token') ?? '';

  const [stand, setStand] = useState<Stand | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState('');
  const [passwort, setPasswort] = useState('');
  const [wiederholung, setWiederholung] = useState('');
  const [gezeigt, setGezeigt] = useState('');
  const [fertig, setFertig] = useState(false);
  const [sendet, setSendet] = useState(false);
  const [kopiert, setKopiert] = useState(false);

  useEffect(() => {
    if (!token) {
      setLaedt(false);
      return;
    }
    fetch(`/api/instanz-zugang/${encodeURIComponent(token)}`)
      .then(async (a) => {
        if (!a.ok) throw new Error((await a.json().catch(() => ({}))).message ?? '');
        return a.json();
      })
      .then(setStand)
      .catch((e: Error) =>
        setFehler(e.message || 'Dieser Link ist abgelaufen oder wurde bereits benutzt.'),
      )
      .finally(() => setLaedt(false));
  }, [token]);

  async function einloesen(mitPasswort?: string) {
    setFehler('');
    setSendet(true);
    try {
      const antwort = await fetch(`/api/instanz-zugang/${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(mitPasswort ? { passwort: mitPasswort } : {}),
      });
      const daten = await antwort.json().catch(() => ({}));
      if (!antwort.ok) {
        setFehler(daten.message ?? 'Das hat nicht geklappt.');
        return;
      }
      if (daten.passwort) setGezeigt(daten.passwort);
      else setFertig(true);
    } catch {
      setFehler('Die Verbindung ist abgebrochen. Bitte noch einmal versuchen.');
    } finally {
      setSendet(false);
    }
  }

  function absenden(e: React.FormEvent) {
    e.preventDefault();
    if (passwort !== wiederholung) {
      setFehler('Die beiden Eingaben stimmen nicht überein.');
      return;
    }
    if (passwort.length < MINDESTLAENGE) {
      setFehler(`Bitte wählen Sie mindestens ${MINDESTLAENGE} Zeichen.`);
      return;
    }
    void einloesen(passwort);
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-sunk p-4">
      <div className="w-full max-w-md">
        <img src="/juni-logo.svg" alt="juni" className="h-8 mx-auto mb-8" />
        <div className="bg-surface border border-slate-200 rounded-xl shadow-sm p-6">
          {laedt ? (
            <p className="text-sm text-slate-500 text-center py-4">Einen Moment …</p>
          ) : !token || (!stand && fehler) ? (
            <div className="text-center py-4">
              <ShieldAlert className="mx-auto text-amber-500 mb-3" size={40} />
              <h1 className="text-lg font-semibold text-slate-900 mb-1">Link nicht mehr gültig</h1>
              <p className="text-sm text-slate-500">
                {fehler || 'Dieser Link ist abgelaufen oder wurde bereits benutzt.'}
              </p>
              <p className="text-sm text-slate-500 mt-3">
                Bitte fordern Sie bei uns einen neuen an.
              </p>
            </div>
          ) : gezeigt ? (
            /* ── Der Weg „anzeigen", nach dem Klick ──────────────────────── */
            <div>
              <h1 className="text-lg font-semibold text-slate-900 mb-1">Ihr Passwort</h1>
              <p className="text-sm text-slate-500 mb-4">
                Für <strong className="text-slate-900">{stand?.adresse}</strong> auf{' '}
                <strong className="text-slate-900">{stand?.instanz}</strong>.
              </p>
              <div className="flex items-stretch gap-2 mb-4">
                <code className="flex-1 bg-slate-900 text-surface rounded-lg px-3 py-2.5 text-sm break-all">
                  {gezeigt}
                </code>
                <button
                  type="button"
                  onClick={() => {
                    void navigator.clipboard.writeText(gezeigt);
                    setKopiert(true);
                  }}
                  className="px-3 rounded-lg border border-slate-200 text-slate-500 hover:text-slate-900"
                  title="Kopieren"
                >
                  <Copy size={16} />
                </button>
              </div>
              {kopiert && <p className="text-xs text-brand-600 mb-3">Kopiert.</p>}
              {/* Deutlich, und nicht kleingedruckt: wer die Seite schliesst,
                  ohne es zu sichern, braucht einen neuen Link. */}
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                Bitte jetzt sichern — nach dem Schliessen dieser Seite ist das Passwort hier
                nicht mehr abrufbar. Ändern Sie es nach der ersten Anmeldung.
              </p>
            </div>
          ) : fertig ? (
            <div className="text-center py-4">
              <CheckCircle className="mx-auto text-brand-600 mb-3" size={40} />
              <h1 className="text-lg font-semibold text-slate-900 mb-1">Passwort gesetzt</h1>
              <p className="text-sm text-slate-500">
                Sie können sich jetzt auf{' '}
                <a
                  href={`https://${stand?.instanz}`}
                  className="text-brand-600 hover:underline"
                  rel="noreferrer"
                >
                  {stand?.instanz}
                </a>{' '}
                anmelden.
              </p>
            </div>
          ) : stand?.modus === 'ANZEIGEN' ? (
            /* ── Der Weg „anzeigen", davor ───────────────────────────────── */
            <div>
              <KeyRound className="text-brand-600 mb-3" size={28} />
              <h1 className="text-lg font-semibold text-slate-900 mb-1">Zugangsdaten</h1>
              <p className="text-sm text-slate-500 mb-4">
                Für <strong className="text-slate-900">{stand.adresse}</strong> auf{' '}
                <strong className="text-slate-900">{stand.instanz}</strong>. Das Passwort lässt
                sich <strong>einmal</strong> anzeigen.
              </p>
              {fehler && <p className="text-sm text-red-600 mb-3">{fehler}</p>}
              <button
                type="button"
                onClick={() => void einloesen()}
                disabled={sendet}
                className="w-full bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white rounded-lg py-2.5 text-sm font-semibold"
              >
                {sendet ? 'Einen Moment …' : 'Passwort anzeigen'}
              </button>
            </div>
          ) : (
            /* ── Der Weg „setzen" ────────────────────────────────────────── */
            <form onSubmit={absenden}>
              <KeyRound className="text-brand-600 mb-3" size={28} />
              <h1 className="text-lg font-semibold text-slate-900 mb-1">Passwort vergeben</h1>
              <p className="text-sm text-slate-500 mb-4">
                Für <strong className="text-slate-900">{stand?.adresse}</strong> auf{' '}
                <strong className="text-slate-900">{stand?.instanz}</strong>.
              </p>
              <label className="block text-sm text-slate-500 mb-1">Neues Passwort</label>
              <input
                type="password"
                value={passwort}
                onChange={(e) => setPasswort(e.target.value)}
                autoComplete="new-password"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm mb-3 bg-surface text-slate-900"
              />
              <label className="block text-sm text-slate-500 mb-1">Wiederholung</label>
              <input
                type="password"
                value={wiederholung}
                onChange={(e) => setWiederholung(e.target.value)}
                autoComplete="new-password"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm mb-3 bg-surface text-slate-900"
              />
              <p className="text-xs text-slate-500 mb-3">
                Mindestens {MINDESTLAENGE} Zeichen. Bereits andernorts geleakte Passwörter weist
                das System zurück.
              </p>
              {fehler && <p className="text-sm text-red-600 mb-3">{fehler}</p>}
              <button
                type="submit"
                disabled={sendet}
                className="w-full bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white rounded-lg py-2.5 text-sm font-semibold"
              >
                {sendet ? 'Wird gesetzt …' : 'Passwort setzen'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
